import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { T } from "@/lib/theme";
import { ArrowUpRight } from "lucide-react";
import EMark from "@/components/EMark";

/**
 * LegalLayout — shared editorial layout for all Erenown legal pages.
 *
 * Props:
 *   eyebrow:        short strap, e.g. "PRIVACY POLICY"
 *   title:          large heading
 *   intro:          short paragraph below title
 *   originallyPublished: date string, e.g. "01 September 2020"
 *   lastUpdated:    date string, e.g. "16 October 2021"
 *   sections:       [{ id, label, body: (string | { type: 'list', items: [..] })[] }]
 *
 * Renders:
 *   - Quiet hero with eyebrow, title, dates, intro, meta strip
 *   - 12-col body: sticky TOC on the left, content on the right
 *   - Erenown Entities reference card at the foot
 */
const ROMAN = ["I","II","III","IV","V","VI","VII","VIII","IX","X","XI","XII","XIII","XIV","XV"];

export default function LegalLayout({ eyebrow, title, intro, originallyPublished, lastUpdated, sections }) {
  const [active, setActive] = useState(sections[0]?.id || "");

  // Scrollspy: highlight the TOC entry whose section is closest to the top.
  useEffect(() => {
    const handler = () => {
      const offsets = sections
        .map((s) => {
          const el = document.getElementById(`legal-${s.id}`);
          if (!el) return null;
          return { id: s.id, top: el.getBoundingClientRect().top };
        })
        .filter(Boolean);
      const visible = offsets
        .filter((o) => o.top - 140 <= 0)
        .sort((a, b) => b.top - a.top)[0];
      if (visible) setActive(visible.id);
    };
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, [sections]);

  return (
    <div data-testid="legal-page" style={{ background: T.bg, color: T.text }}>
      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{ background: T.bg }}
        data-testid="legal-hero"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle at center, ${T.border}66 1px, transparent 1px)`,
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black, transparent 82%)",
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16">
          <div
            className="flex items-center gap-4 font-plexmono text-[10.5px] tracking-[0.22em] uppercase"
            style={{ color: T.muted }}
          >
            <span className="inline-flex items-center gap-2"><EMark /> · LEGAL · MMXX</span>
            <span className="flex-1 h-px" style={{ background: T.border }} />
            <span style={{ color: T.accent }}>{eyebrow}</span>
          </div>

          <h1
            className="font-fraunces font-light text-5xl sm:text-6xl lg:text-[68px] leading-[1.04] tracking-[-0.015em] mt-10 max-w-3xl"
            style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}
          >
            {title}
          </h1>

          <div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-3 font-plexmono text-[10.5px] tracking-[0.28em] uppercase" style={{ color: T.muted }}>
            <div className="flex items-center gap-3">
              <span>Originally published</span>
              <span style={{ color: T.warm }}>{originallyPublished}</span>
            </div>
            <span className="hidden md:inline h-3 w-px" style={{ background: T.border }} />
            <div className="flex items-center gap-3">
              <span>Last updated</span>
              <span style={{ color: T.accent }}>{lastUpdated}</span>
            </div>
          </div>

          {intro && (
            <p
              className="mt-8 max-w-2xl font-intertight text-[16px] leading-[1.9]"
              style={{ color: T.textSoft }}
            >
              {intro}
            </p>
          )}
        </div>
      </section>

      {/* BODY */}
      <section
        className="border-t"
        style={{ borderColor: T.border, background: T.surface }}
        data-testid="legal-body"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-12 gap-10 lg:gap-16">
          {/* TOC */}
          <aside className="col-span-12 lg:col-span-3" data-testid="legal-toc">
            <div className="lg:sticky lg:top-28">
              <div
                className="font-plexmono text-[10px] tracking-[0.32em] uppercase mb-5"
                style={{ color: T.muted }}
              >
                Index
              </div>
              <ol className="space-y-3">
                {sections.map((s, i) => {
                  const isActive = s.id === active;
                  return (
                    <li key={s.id}>
                      <a
                        href={`#legal-${s.id}`}
                        data-testid={`legal-toc-${s.id}`}
                        className="group flex items-baseline gap-3 font-plexmono text-[10.5px] tracking-[0.24em] uppercase transition-colors"
                        style={{
                          color: isActive ? T.accent : T.mutedSoft,
                        }}
                      >
                        <span
                          className="tabular-nums"
                          style={{ color: isActive ? T.warm : T.muted, minWidth: "1.6rem" }}
                        >
                          {ROMAN[i]}.
                        </span>
                        <span className="group-hover:text-white transition-colors">
                          {s.label}
                        </span>
                      </a>
                    </li>
                  );
                })}
              </ol>
            </div>
          </aside>

          {/* SECTIONS */}
          <article className="col-span-12 lg:col-span-9 space-y-14 md:space-y-16">
            {sections.map((s, i) => (
              <section
                key={s.id}
                id={`legal-${s.id}`}
                data-testid={`legal-section-${s.id}`}
                className="scroll-mt-28"
              >
                <div className="flex items-center gap-4">
                  <span
                    className="font-plexmono text-[10.5px] tracking-[0.32em] uppercase"
                    style={{ color: T.warm }}
                  >
                    {ROMAN[i]}
                  </span>
                  <span className="h-px w-10" style={{ background: T.border }} />
                </div>

                <h2
                  className="font-fraunces font-normal text-3xl md:text-4xl mt-5 leading-[1.18]"
                  style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}
                >
                  {s.label}
                </h2>

                <div
                  className="mt-6 max-w-[60ch] space-y-5 font-intertight text-[15.5px] leading-[1.95]"
                  style={{ color: T.textSoft }}
                >
                  {s.body.map((b, k) => {
                    if (typeof b === "string") {
                      return <p key={k}>{b}</p>;
                    }
                    if (b.type === "list") {
                      return (
                        <ul key={k} className="space-y-2.5 pl-0">
                          {b.items.map((item, j) => (
                            <li
                              key={j}
                              className="flex gap-3"
                            >
                              <span
                                className="font-plexmono tabular-nums text-[11px] tracking-[0.2em] mt-1.5"
                                style={{ color: T.warm, minWidth: "2rem" }}
                              >
                                {String(j + 1).padStart(2, "0")}
                              </span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    if (b.type === "mono") {
                      return (
                        <div
                          key={k}
                          className="border-l pl-5 py-2 font-plexmono text-[12.5px] leading-[1.85] tracking-[0.04em]"
                          style={{ borderColor: T.warm, color: T.text }}
                        >
                          {b.lines.map((ln, j) => (
                            <div key={j}>{ln}</div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </section>
            ))}
          </article>
        </div>
      </section>

      {/* ENTITIES REFERENCE CARD */}
      <section
        className="border-t"
        style={{ borderColor: T.border, background: T.bg }}
        data-testid="legal-entities"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div
              className="font-plexmono text-[10.5px] tracking-[0.32em] uppercase"
              style={{ color: T.accent }}
            >
              Erenown Entities
            </div>
            <p
              className="mt-5 max-w-xs font-intertight text-[14px] leading-[1.85]"
              style={{ color: T.textSoft }}
            >
              Operating through independently established Erenown entities in the United Kingdom and Kosovo.
            </p>
          </div>

          <div className="col-span-12 md:col-span-4" data-testid="legal-entity-uk">
            <div
              className="font-plexmono text-[10px] tracking-[0.28em] uppercase mb-3"
              style={{ color: T.muted }}
            >
              United Kingdom
            </div>
            <div className="font-fraunces text-[20px] leading-[1.4]" style={{ color: T.text }}>
              Erenown Ltd
            </div>
            <div className="mt-2 font-intertight text-[13px] leading-[1.85]" style={{ color: T.mutedSoft }}>
              Registered in England and Wales<br />
              Company No. 12845592<br />
              Registered Office:<br />
              83 St Marks Road, Enfield,<br />
              EN1 1BJ, United Kingdom
            </div>
          </div>

          <div className="col-span-12 md:col-span-4" data-testid="legal-entity-kosovo">
            <div
              className="font-plexmono text-[10px] tracking-[0.28em] uppercase mb-3"
              style={{ color: T.muted }}
            >
              Kosovo
            </div>
            <div className="font-fraunces text-[20px] leading-[1.4]" style={{ color: T.text }}>
              Erenown LLC
            </div>
            <div className="mt-2 font-intertight text-[13px] leading-[1.85]" style={{ color: T.mutedSoft }}>
              Ulpianë, C2, H-III, Nr.6<br />
              Bulevardi “Dëshmorët e Kombit”,<br />
              Prishtinë 10000,<br />
              Republic of Kosovo
            </div>
          </div>
        </div>

        {/* Contact strip */}
        <div className="border-t" style={{ borderColor: T.border }}>
          <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-10 flex flex-wrap items-center gap-6 justify-between">
            <div>
              <div
                className="font-plexmono text-[10px] tracking-[0.28em] uppercase mb-2"
                style={{ color: T.muted }}
              >
                Contact
              </div>
              <a
                href="mailto:info@erenown.com"
                data-testid="legal-email"
                className="font-fraunces text-xl md:text-2xl border-b inline-block"
                style={{ color: T.text, borderColor: T.accent }}
              >
                info@erenown.com
              </a>
            </div>
            <Link
              to="/contact"
              data-testid="legal-cta"
              className="group inline-flex items-center gap-3 px-7 py-4 font-plexmono text-[11px] tracking-[0.3em] uppercase border transition-colors"
              style={{ borderColor: `${T.warm}66`, color: T.warm }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${T.warm}10`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              Start a Conversation
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
